import { Box, MenuItem, Select, Stack, Typography } from '@mui/material'
import { C1Button } from 'c1ui'
import { C1Chip } from 'c1ui/components/C1Chip'
import { C1LoadingButton } from 'components/core/button'
import { C1DrawerInner } from 'components/core/drawer/C1DrawerInner'
import { APIError } from 'components/core/errors/api'
import { SearchUsers } from 'components/core/search/users'
import { TextField } from 'components/core/textfield'
import { ClosableProps } from 'components/no-ui/context/modal-provider'
import { C1EntitlementDisplayName } from 'components/pages/entitlement/display-name'
import { ResourceTypeDisplayName } from 'components/pages/entitlement/resource-type'
import { useAlert } from 'hooks/useAlert'
import { AppEntitlementValue } from 'lib/actions/apps/app-entitlement'
import {
    AppResourceType,
    CreateManuallyManagedResourceRequest,
    ManuallyManagedEntitlementSlug,
    ManuallyManagedResourceType,
} from 'pbts/c1/api/app/v1/app_resource'
import { User, UserStatus } from 'pbts/c1/api/user/v1/user'
import React, { FC, useCallback, useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { createManuallyManagedAppResource } from 'redux_toolkit/app-resources'

type DrawerHeaderProps = {
    req: CreateManuallyManagedResourceRequest
    setCanClose: (canClose: boolean) => void
    close: () => void
}

// Displays the footer and action area for the drawer to create a custom entitlement.
const DrawerFooter = ({
    req,
    setCanClose,
    close,
}: DrawerHeaderProps): React.ReactElement => {
    const intl = useIntl()
    const toast = useAlert()

    const [disableCreate, setDisableCreate] = useState<boolean>(true)

    const { mutate: create, inProgress } = createManuallyManagedAppResource()

    useEffect(() => {
        if (req.displayName) {
            setDisableCreate(false)
        } else {
            setDisableCreate(true)
        }
    }, [req])

    const onClickCreate = useCallback(
        async (): Promise<void> => {
            setDisableCreate(true)
            setCanClose(false)

            try {
                await create(req)

                toast(intl.formatMessage({
                    id: 'component_create_custom_entitlement_success',
                    defaultMessage: 'Successfully created entitlement',
                }), 'success')
            } catch (err) {
                toast(intl.formatMessage({
                    id: 'component_create_custom_entitlement_failure',
                    defaultMessage: 'Failed to create entitlement: {error}',
                }, { error: (err as Error).message }), 'error')
            }

            setCanClose(true)
            close()
        },
        [
            req,
            create,
            setCanClose,
            close,
            toast,
            intl,
        ],
    )

    return <Stack direction="row" spacing={2} justifyContent="flex-end">
        <C1Button onClick={close} variant="outlined" disabled={inProgress}>
            <FormattedMessage id="component_create_custom_entitlement_cancel" defaultMessage="Cancel" />
        </C1Button>
        <C1LoadingButton onClick={onClickCreate} color="primary" variant="contained" loading={inProgress} disabled={disableCreate}>
            <FormattedMessage id="component_create_custom_entitlement_create" defaultMessage="Create" />
        </C1LoadingButton>
    </Stack>
}

// Displays the resource type that will be used to create the custom entitlement. Currently, we only
// support roles as the resource type.
const PickResourceType: FC<{
    onResourceTypeChange: (rt: ManuallyManagedResourceType) => void
}> = ({ onResourceTypeChange }): React.ReactElement => {
    const supportedTypes = ['role', 'group', 'license', 'project']

    const [selectedType, setSelectedType] = useState('role')

    const onChange = (rt: string): void => {
        setSelectedType(rt)
        switch (rt) {
            case 'role':
                onResourceTypeChange(ManuallyManagedResourceType.ROLE)
                break
            case 'group':
                onResourceTypeChange(ManuallyManagedResourceType.GROUP)
                break
            case 'license':
                onResourceTypeChange(ManuallyManagedResourceType.LICENSE)
                break
            case 'project':
                onResourceTypeChange(ManuallyManagedResourceType.PROJECT)
                break
            default:
                onResourceTypeChange(ManuallyManagedResourceType.ROLE)
        }
    }

    const renderType = (rt: string): React.ReactElement => {
        return <ResourceTypeDisplayName resourceType={AppResourceType.create({ displayName: rt })} />
    }

    return <Stack spacing={1} width={300}>
        <Typography variant="h6">
            <FormattedMessage
                id="component_create_custom_entitlement_resource_type"
                defaultMessage="Resource type"
            />
        </Typography>
        <Select
            value={selectedType}
            renderValue={renderType}
            onChange={(e): void => { onChange(e.target.value) }}
            autoWidth={false}
            sx={{ backgroundColor: 'background.paper' }}
            size="small"
        >
            {
                supportedTypes.map((rt: string) => (
                    <MenuItem key={rt} value={rt}>
                        <Stack spacing={2} direction="row" alignItems="center">
                            <ResourceTypeDisplayName resourceType={AppResourceType.create({ displayName: rt })} />
                        </Stack>
                    </MenuItem>
                ))
            }
        </Select>
    </Stack>
}

// Allows the user to set the name of the resource they would like to create.
const ResourceNameInput: FC<{ onResourceNameChange: (resourceName: string) => void }> = ({ onResourceNameChange }): React.ReactElement => {
    const intl = useIntl()

    const onChange = useCallback((e: React.FocusEvent<HTMLInputElement>): void => {
        onResourceNameChange(e.target.value)
    }, [onResourceNameChange])

    return <TextField
        id="component_create_custom_entitlement_name"
        placeholder={intl.formatMessage({
            id: 'component_create_custom_entitlement_name_placeholder',
            defaultMessage: 'What do you want to call your resource?',
        })}
        onChange={onChange}
        label={intl.formatMessage({
            id: 'component_create_custom_entitlement_name_label',
            defaultMessage: 'Name',
        })}
        required
    />
}

type ResourceDescriptionInputProps = {
    onResourceDescChange: (resourceDesc: string) => void
}

// Allows the user to set the description of the resource they would like to create.
const ResourceDescriptionInput: FC<ResourceDescriptionInputProps> = ({ onResourceDescChange }): React.ReactElement => {
    const intl = useIntl()

    const onChange = useCallback((e: React.FocusEvent<HTMLInputElement>): void => {
        onResourceDescChange(e.target.value)
    }, [onResourceDescChange])

    return <TextField
        multiline
        minRows={2}
        id="component_create_custom_entitlement_description"
        placeholder={intl.formatMessage({
            id: 'component_create_custom_entitlement_description_placeholder',
            defaultMessage: 'What is this resource all about?',
        })}
        onChange={onChange}
        label={intl.formatMessage({
            id: 'component_create_custom_entitlement_description_label',
            defaultMessage: 'Description',
        })}
        required
    />
}

// Displays the default entitlement that will be created. Currently, we only support member as the
// entitlement for the resource.
const DefaultEntitlementDisplay: FC<{ slug: string }> = ({ slug }): React.ReactElement => {
    return <Stack spacing={1}>
        <Typography variant="h6">
            <FormattedMessage
                id="component_create_custom_entitlement_default_entitlement"
                defaultMessage="Default entitlement"
            />
        </Typography>
        <Box sx={{ border: 1, borderColor: 'lightgrey', borderRadius: 1, backgroundColor: 'primary' }} p={1} width={150} height={35}>
            <C1Chip label={slug} />
        </Box>
    </Stack>
}

// Allows the user to pick the linked entitlement for the custom entitlement.
const PickLinkedEntitlement: FC<{
    ents: AppEntitlementValue[],
    onSelectedEnt: (entId: string | null, appId: string | null) => void,
}> = ({ ents, onSelectedEnt }): React.ReactElement => {
    const intl = useIntl()
    const [selectedEntitlement, setSelectedEntitlement] = useState('')

    const onChange = (id: string): void => {
        const appId = ents.find((appEntitlement: AppEntitlementValue) => appEntitlement.id === id)?.appId || ''
        setSelectedEntitlement(id)
        onSelectedEnt(id, appId)
    }

    // Reset the selected entitlement when the dropdown options change.
    useEffect(() => {
        setSelectedEntitlement('')
    }, [ents])

    const renderEntitlement = (id: string): React.ReactElement => {
        const ent = ents.find((appEntitlement: AppEntitlementValue) => appEntitlement.id === id)
        if (ent) {
            return <C1EntitlementDisplayName entitlement={ent} readOnly />
        }
        return <APIError message="Could not find entitlement" />
    }

    return <Stack spacing={1} width={350}>
        <Typography variant="h6">
            <FormattedMessage
                id="component_create_custom_entitlement_linked_entitlement"
                defaultMessage="IDP linked entitlement (optional)"
            />
        </Typography>
        {ents.length === 0 ? (
            <Box>
                <TextField
                    id="component_create_custom_entitlement_linked_ents_empty"
                    placeholder={intl.formatMessage({
                        id: 'component_create_custom_entitlement_linked_entitlement_empty',
                        defaultMessage: 'We could not find any linked IDP entitlements.',
                    })}
                    disabled
                />
            </Box>
        ) : (
            <Select
                value={selectedEntitlement}
                renderValue={renderEntitlement}
                onChange={(e): void => { onChange(e.target.value) }}
                autoWidth={false}
                sx={{ backgroundColor: 'background.paper' }}
                size="small"
            >
                {
                    ents.map((appEntitlement: AppEntitlementValue) => (
                        <MenuItem key={appEntitlement.id} value={appEntitlement.id}>
                            <Stack spacing={2} direction="row" alignItems="center">
                                <C1EntitlementDisplayName entitlement={appEntitlement} readOnly />
                            </Stack>
                        </MenuItem>
                    ))
                }
            </Select>
        )}
    </Stack>
}

// Allows the user to pick the resource owner for the custom entitlement.
const PickResourceOwner: FC<{ onSelectedUsers: (users: User[]) => void }> = ({ onSelectedUsers }): React.ReactElement => {
    return <Stack width={350}>
        <Typography variant="h6">
            <FormattedMessage
                id="component_create_custom_entitlement_resource_owners"
                defaultMessage="Resource owner (optional)"
            />
        </Typography>
        <SearchUsers
            onSelected={onSelectedUsers}
            baseRequest={{
                userStatuses: [UserStatus.ENABLED],
            }}
        />
    </Stack>
}

type CreateCustomEntitlementProps = {
    appId: string;
    linkedEnts: AppEntitlementValue[];
    setCanClose: (canClose: boolean) => void;
} & ClosableProps

export const CreateCustomEntitlement = ({
    appId,
    linkedEnts,
    setCanClose,
    close,
}: CreateCustomEntitlementProps): React.ReactElement => {
    const [entSlug, setEntSlug] = useState('member')
    const [createRequest, setCreateRequest] = useState<CreateManuallyManagedResourceRequest>(
        CreateManuallyManagedResourceRequest.create({ appId }),
    )

    const onSelectResourceType = (rt: ManuallyManagedResourceType): void => {
        const slug = (
            rt === ManuallyManagedResourceType.LICENSE ? ManuallyManagedEntitlementSlug.ASSIGNED : ManuallyManagedEntitlementSlug.MEMBER
        )

        switch (slug) {
            case ManuallyManagedEntitlementSlug.ASSIGNED:
                setEntSlug('assigned')
                break
            case ManuallyManagedEntitlementSlug.MEMBER:
                setEntSlug('member')
                break
            default:
                setEntSlug('member')
        }

        setCreateRequest((prev) => CreateManuallyManagedResourceRequest.create({ ...prev, resourceType: rt, entitlementSlug: slug }))
    }

    const onSelectResourceName = (name: string): void => {
        setCreateRequest((prev) => CreateManuallyManagedResourceRequest.create({ ...prev, displayName: name }))
    }
    const onSelectResourceDesc = (desc: string): void => {
        setCreateRequest((prev) => CreateManuallyManagedResourceRequest.create({ ...prev, description: desc }))
    }
    const onSelectLinkedEntitlement = (entId: string | null, entAppId: string | null): void => {
        if (entId === null || entAppId === null) {
            return
        }
        setCreateRequest((prev) => CreateManuallyManagedResourceRequest.create(
            { ...prev, linkedIdpEntitlementAppId: entAppId, linkedIdpEntitlementId: entId },
        ))
    }
    const onSelectResourceOwners = (users: User[]): void => {
        setCreateRequest((prev) => CreateManuallyManagedResourceRequest.create(
            { ...prev, resourceOwnerUserIds: users.map((user: User) => user.id) },
        ))
    }

    return <C1DrawerInner>
        <C1DrawerInner.Header>
            <Stack spacing={2}>
                <Typography variant="h2" component="div">
                    <FormattedMessage
                        id="component_create_virtual_entitlement_title"
                        defaultMessage="Create a virtual entitlement"
                    />
                </Typography>
                <Typography>
                    <FormattedMessage
                        id="component_create_custom_entitlement_subtitle"
                        defaultMessage="Users will be able to request access to this entitlement, but it will exist only in ConductorOne."
                    />
                </Typography>
            </Stack>
        </C1DrawerInner.Header>
        <C1DrawerInner.Content>
            <Stack spacing={4}>
                <PickResourceType onResourceTypeChange={onSelectResourceType} />
                <ResourceNameInput onResourceNameChange={onSelectResourceName} />
                <ResourceDescriptionInput onResourceDescChange={onSelectResourceDesc} />
                <DefaultEntitlementDisplay slug={entSlug} />
                <PickLinkedEntitlement ents={linkedEnts} onSelectedEnt={onSelectLinkedEntitlement} />
                <PickResourceOwner onSelectedUsers={onSelectResourceOwners} />
            </Stack>
        </C1DrawerInner.Content>
        <C1DrawerInner.Footer>
            <DrawerFooter
                req={createRequest}
                setCanClose={setCanClose}
                close={close}
            />
        </C1DrawerInner.Footer>
    </C1DrawerInner>
}
